/**
 * The booking detail's words and rules, shared by the Mini App's BookingDetailPage and the
 * website's booking pages (web-account-pages D4, shared-client-screens D4 "Booking"): the
 * status pill, the hero's when line, the payment row in each of the booking's states (paid,
 * due, due to confirm an approved request, collected after approval, refunded with its
 * ledger), the one action a booking offers, what a cancel cancels and whether the cancel
 * window reads the refund rule fresh. Pure; dates in the browser's zone, as every client
 * surface reads.
 */
import { formatCurrency } from '../../components/payment/formatCurrency';
import type { BookingDetail, BookingDetailStatus } from '../../types/bookingDetail';
import { formatFinishBy } from '../../utils/waitingBooking';
import { isFormsDue } from '../BookingHubPage/homeFormat';
import { isLinkCard, LINK_LABEL } from '@telify/shared';

// ── Copy tables ──────────────────────────────────────────────────────────────
export const PILL: Record<BookingDetailStatus, { label: string; muted: boolean }> = {
  CONFIRMED: { label: 'Confirmed', muted: false },
  PENDING_PAYMENT: { label: 'Payment due', muted: false },
  PENDING_APPROVAL: { label: 'Pending approval', muted: false },
  WAITLISTED: { label: 'Waitlisted', muted: false },
  COMPLETED: { label: 'Completed', muted: true },
  // A cancelled booking's pill reads at full strength; its Cancelled line says the rest (card 4 walk, Gabriel 2026-10-31).
  CANCELLED: { label: 'Cancelled', muted: false },
};
const METHOD_LABELS: Record<string, string> = { CASH: 'Cash', VENMO: 'Venmo', ZELLE: 'Zelle', CARD: 'Card', OTHER: 'Other' };

export const isRequest = (s: BookingDetailStatus) => s === 'PENDING_APPROVAL' || s === 'WAITLISTED';

/** The status pill: a held booking reads Forms due only when forms alone are left, Payment due while payment is owed (Gabriel 2026-10-07, 2026-10-23). */
export function statusPill(b: Pick<BookingDetail, 'status' | 'outstanding'>): { label: string; muted: boolean } {
  return b.status === 'PENDING_PAYMENT' && isFormsDue({ status: b.status, outstanding: b.outstanding })
    ? { label: 'Forms due', muted: false }
    : PILL[b.status];
}

/** A booking's page whose read failed for any reason but not-found (rows B2a, B2b): Try again, in the app's words. */
export const BOOKING_READ_FAILED = { title: 'Something went wrong', text: 'Please try again.', retryLabel: 'Try again' } as const;

/**
 * The line a booking's page shows for its state, in the app's words (manual-booking-redesign D13;
 * web-account-pages D11): a booking the studio made that waits on the client and its deadline, an
 * approved request to pay, forms under review, a request submitted, the waitlist, a declined request,
 * and a cancelled booking with what happened to its money (card 4 walk, Gabriel 2026-10-31). The app
 * sets the title in bold except the waitlist's (`bold` false).
 */
export function bookingNotice(
  b: Pick<BookingDetail, 'status' | 'completeBy' | 'bookedByFirstName' | 'declinedAt' | 'payment'>,
  businessName: string,
): { icon: 'info' | 'clock'; title: string; text: string; bold: boolean } | null {
  if (b.status === 'CANCELLED') return cancelledNotice(b, businessName);
  if (b.status === 'PENDING_PAYMENT' && b.completeBy) {
    return { icon: 'info', title: businessName ? `${businessName} booked this for you.` : 'Booked for you.', text: `Finish by ${formatFinishBy(b.completeBy)} to keep it.`, bold: true };
  }
  if (b.status === 'PENDING_PAYMENT') return { icon: 'info', title: 'Your request was approved!', text: 'Complete payment to secure your spot.', bold: true };
  if (b.status === 'PENDING_APPROVAL' && b.completeBy) {
    // The admin who made a manual booking reviews its forms; the business when the admin has no first name.
    const reviewer = b.bookedByFirstName || businessName;
    return { icon: 'clock', title: 'Forms received.', text: `${reviewer ? `${reviewer} is reviewing them.` : "We're reviewing them."} You'll hear back in Telegram.`, bold: true };
  }
  if (b.status === 'PENDING_APPROVAL') return { icon: 'clock', title: 'Request submitted.', text: "We'll review it. You'll hear back in Telegram.", bold: true };
  if (b.status === 'WAITLISTED') return { icon: 'clock', title: "You're on the waitlist.", text: "We'll message you if a spot opens.", bold: false };
  return null;
}

/** A declined request is cancelled in the lists; its page says the studio declined it. A cancelled booking says what became of its money. */
function cancelledNotice(b: Pick<BookingDetail, 'declinedAt' | 'payment'>, businessName: string) {
  const studio = businessName || 'The studio';
  if (b.declinedAt) return { icon: 'info' as const, title: 'Request declined.', text: `${studio} could not take this request.`, bold: true };
  const p = b.payment;
  const say = (text: string) => ({ icon: 'info' as const, title: 'Cancelled.', text, bold: true });
  if (p && p.refunds.length > 0) {
    const refunded = formatCurrency(p.refundAmount ?? p.refunds.reduce((sum, line) => sum + line.amount, 0));
    return say(p.refunds.some((line) => line.status === 'ON_ITS_WAY') ? `Your ${refunded} refund is on its way.` : `Your ${refunded} was refunded.`);
  }
  if (p?.status === 'REFUNDED' && p.refundAmount != null) return say(`Your ${formatCurrency(p.refundAmount)} was refunded.`);
  if (p?.status === 'PAID' && p.amount != null) return say(`${studio} handles any refund of your ${formatCurrency(p.amount)}.`);
  return say('Nothing was charged.');
}

/**
 * The one action a booking offers (manual-booking-redesign D13; shared-client-screens rows B3,
 * B4, B5b). A booking an admin made that still owes a form or its registration opens the
 * Complete step; once only payment is left it is Pay now; a past or cancelled one books again,
 * an event from the events since it has no service page. Each surface maps the kind to its page.
 */
export type BookingErrand =
  | { kind: 'complete'; label: 'Complete booking' | 'Register' }
  | { kind: 'pay'; label: string }
  | { kind: 'bookAgain'; label: 'Book again'; to: 'service' | 'events' };

/** What the rule reads of a booking, so the website's marks in its lists (web-studio-sites 6.6) give the same step. */
export type ErrandFacts = Pick<BookingDetail, 'status' | 'completeBy' | 'outstanding'> & {
  readonly payment: Pick<NonNullable<BookingDetail['payment']>, 'amount'> | null;
  readonly service: Pick<BookingDetail['service'], 'type'>;
};

export function bookingErrand(b: ErrandFacts): BookingErrand | null {
  const owed = b.outstanding;
  const waiting = b.status === 'PENDING_PAYMENT' && !!b.completeBy;
  if (waiting && (!owed || owed.forms > 0 || !owed.payment)) {
    return { kind: 'complete', label: owed?.registration && owed.forms === 1 && !owed.payment ? 'Register' : 'Complete booking' };
  }
  if (b.status === 'PENDING_PAYMENT') {
    const amount = b.payment?.amount;
    return { kind: 'pay', label: amount == null ? 'Pay now' : `Pay ${formatCurrency(amount)} now` };
  }
  if (b.status === 'COMPLETED' || b.status === 'CANCELLED') {
    return { kind: 'bookAgain', label: 'Book again', to: b.service.type === 'event' ? 'events' : 'service' };
  }
  return null;
}

/** What a cancel cancels: leaving a waitlist, withdrawing a request, or the booking itself. */
export type CancelKind = 'booking' | 'request' | 'waitlist';

export function cancelKindOf(b: Pick<BookingDetail, 'status'>): CancelKind {
  return b.status === 'WAITLISTED' ? 'waitlist' : isRequest(b.status) ? 'request' : 'booking';
}

/** The cancel window reads the refund rule that applies now whenever something was or may be refunded. */
export function needsFreshRefundPolicy(b: Pick<BookingDetail, 'refundPolicySummaryLine' | 'payment'>): boolean {
  return Boolean(b.refundPolicySummaryLine) || b.payment?.status === 'PAID' || b.payment?.status === 'REFUNDED';
}

// ── Date/countdown helpers (browser tz — CWR client source) ──────────────────
export function fmtDayDate(d: Date) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(d);
}
export function fmtTime(d: Date) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(d);
}
function fmtShortDate(iso: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
}
export function heroWhenLabel(booking: BookingDetail): string | null {
  const d = new Date(booking.scheduledAt);
  if (booking.status === 'CANCELLED') return `was scheduled for ${fmtDayDate(d)}`;
  if (booking.status === 'COMPLETED') return fmtDayDate(d);
  if (isRequest(booking.status)) return null;
  // CONFIRMED / PENDING_PAYMENT — countdown (future) or fall back to the date.
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  if (d.getTime() < now.getTime()) return fmtDayDate(d);
  if (days <= 0) return `Today at ${fmtTime(d)}`;
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

export type RefundLedgerLine = { onItsWay: boolean; when: string; amount: number };
export type PaymentRow = {
  val: string;
  sub?: string | null;
  amount?: number | null;
  amountDim?: boolean;
  /** client-booking-refund-details (layout A): refund lines, oldest first, then the net paid. */
  ledger?: { lines: RefundLedgerLine[]; net: number | null };
};

function cardLabel(cardBrand: string | null, cardLast4: string | null): string | null {
  // A Link payment funded by a bank account reads Link: its card digits are made up (wallets plan).
  if (isLinkCard(cardBrand)) return LINK_LABEL;
  if (!cardBrand || !cardLast4) return null;
  return `${cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1)} •••• ${cardLast4}`;
}

function maskedCardLabel(payment: NonNullable<BookingDetail['payment']>): string | null {
  return payment.provider === 'STRIPE' ? cardLabel(payment.cardBrand, payment.cardLast4) : null;
}

/** "Paid · Visa •••• 4242 · Oct 8": the receipt's first line. */
function paidLine(p: NonNullable<BookingDetail['payment']>): string {
  const method = maskedCardLabel(p) ?? (p.method ? METHOD_LABELS[p.method] ?? p.method : null);
  return ['Paid', method, p.paidAt ? fmtShortDate(p.paidAt) : null].filter(Boolean).join(' · ');
}

/** "Oct 12 · to Visa •••• 4242" for a card refund, "Oct 12 · Cash" for a manual one. */
function refundWhen(line: NonNullable<BookingDetail['payment']>['refunds'][number]): string {
  const where = line.method
    ? METHOD_LABELS[line.method] ?? line.method
    : `to ${cardLabel(line.cardBrand, line.cardLast4) ?? 'Card'}`;
  return `${fmtShortDate(line.at)} · ${where}`;
}

export function derivePaymentRow(booking: BookingDetail): PaymentRow | null {
  const p = booking.payment;
  const s = booking.status;
  // Anything refunded, in any status (a waiting booking can already be paid and refunded):
  // the paid line with the collected amount dimmed, each refund, the net.
  if (p && p.refunds.length > 0) {
    return {
      val: paidLine(p),
      sub: p.reference,
      amount: p.collected,
      amountDim: true,
      ledger: {
        lines: p.refunds.map((line) => ({ onItsWay: line.status === 'ON_ITS_WAY', when: refundWhen(line), amount: line.amount })),
        net: p.netPaid,
      },
    };
  }
  // A waiting manual booking follows its effective payment (manual-booking-redesign D6):
  // collected at the session (the flow's "Pay at the session"), or no row when nothing is payable.
  if (s === 'PENDING_PAYMENT' && booking.outstanding && !booking.outstanding.payment) {
    return p?.amount ? { val: 'Pay at the session', amount: p.amount } : null;
  }
  if (s === 'PENDING_PAYMENT') return { val: 'Due to confirm your booking', amount: p?.amount ?? null };
  if (isRequest(s)) return p ? { val: 'Collected after approval' } : null;
  if (!p) return null;
  // A cancelled booking keeps its paid line until a refund replaces it (card 4 walk, Gabriel 2026-10-31).
  if (p.status === 'PAID') {
    return { val: paidLine(p), sub: p.reference, amount: p.amount ?? null };
  }
  if (p.status === 'REFUNDED') {
    const card = maskedCardLabel(p);
    return { val: ['Refunded', card].filter(Boolean).join(' · '), amount: p.refundAmount };
  }
  if (s === 'CANCELLED') return null;
  if (p.provider === 'MANUAL' && p.status === 'UNPAID' && s === 'CONFIRMED') {
    return { val: 'Due', amount: p.amount ?? null };
  }
  return null;
}
